/**
 * Game Over Scene
 * Death screen with score, stats, and restart option
 */

import Phaser from 'phaser';
import { CONSTANTS } from '../config.js';
import { audioEngine } from '../systems/AudioEngine.js';
import { stateManager } from '../systems/StateManager.js';

export class GameOverScene extends Phaser.Scene {
    constructor() {
        super({ key: 'GameOverScene' });
    }

    init(data) {
        this.finalScore = data.score || 0;
        this.survivalTime = data.survivalTime || 0;
        this.isHighScore = data.isHighScore || false;
    }

    create() {
        // Dark horror background
        this.cameras.main.setBackgroundColor(CONSTANTS.COLORS.DARK_BG);
        this.cameras.main.fadeIn(500);

        // Glitch overlay
        this.createGlitchOverlay();

        // Game Over title
        this.createTitle();

        // Stats display
        this.createStats();

        // Buttons
        this.createButtons();

        // Eerie ambient sound
        this.createAmbience();
    }

    createGlitchOverlay() {
        const centerX = this.scale.width / 2;
        const centerY = this.scale.height / 2;

        // Occasional glitch effect
        this.glitchOverlay = this.add.image(centerX, centerY, 'glitch');
        this.glitchOverlay.setAlpha(0);
        this.glitchOverlay.setDepth(200);
        this.glitchOverlay.setBlendMode(Phaser.BlendModes.ADD);

        // Periodic glitch
        this.time.addEvent({
            delay: Phaser.Math.Between(2000, 5000),
            callback: () => this.playGlitch(),
            loop: true
        });
    }

    playGlitch() {
        this.glitchOverlay.setAlpha(0.3);
        this.cameras.main.shake(100, 0.01);

        this.time.delayedCall(100, () => {
            this.glitchOverlay.setAlpha(0);
        });
    }

    createTitle() {
        const centerX = this.scale.width / 2;

        // "SYSTEM FAILURE" or similar horror text
        this.titleText = this.add.text(centerX, 100, 'REGRESSION ERROR', {
            fontSize: '48px',
            fontFamily: 'Courier New, monospace',
            color: '#FF0000',
            fontStyle: 'bold'
        }).setOrigin(0.5);

        // Subtitle
        this.subtitleText = this.add.text(centerX, 150, 'The Overfitter has claimed you', {
            fontSize: '18px',
            fontFamily: 'Georgia, serif',
            color: '#666666',
            fontStyle: 'italic'
        }).setOrigin(0.5);

        // Glitch animation on title
        this.tweens.add({
            targets: this.titleText,
            x: { from: centerX - 2, to: centerX + 2 },
            duration: 100,
            yoyo: true,
            repeat: -1,
            ease: 'Stepped',
            repeatDelay: 2000
        });
    }

    createStats() {
        const centerX = this.scale.width / 2;
        const statsY = this.scale.height / 2 - 50;

        // Score (time survived in seconds)
        this.add.text(centerX, statsY, `SCORE: ${this.finalScore}`, {
            fontSize: '32px',
            fontFamily: 'Courier New, monospace',
            color: '#FFFFFF',
            fontStyle: 'bold'
        }).setOrigin(0.5);

        // High score indicator
        if (this.isHighScore) {
            this.add.text(centerX, statsY + 40, 'NEW HIGH SCORE!', {
                fontSize: '24px',
                fontFamily: 'Arial',
                color: '#FFD700',
                fontStyle: 'bold'
            }).setOrigin(0.5);

            // Particle celebration
            this.createCelebration();
        }

        // Survival time
        const minutes = Math.floor(this.survivalTime / 60000);
        const seconds = Math.floor((this.survivalTime % 60000) / 1000);
        this.add.text(centerX, statsY + 80, `Survived: ${minutes}:${seconds.toString().padStart(2, '0')}`, {
            fontSize: '18px',
            fontFamily: 'Courier New, monospace',
            color: '#888888'
        }).setOrigin(0.5);

        // All-time stats
        const deaths = stateManager.state.totalDeaths;
        const plays = stateManager.state.totalPlays;
        this.add.text(centerX, statsY + 120, `Total attempts: ${plays} | Deaths: ${deaths}`, {
            fontSize: '14px',
            fontFamily: 'Courier New, monospace',
            color: '#555555'
        }).setOrigin(0.5);
    }

    createCelebration() {
        const centerX = this.scale.width / 2;
        const statsY = this.scale.height / 2 - 50;

        // Golden particles for high score
        for (let i = 0; i < 30; i++) {
            const particle = this.add.circle(
                centerX + Phaser.Math.Between(-200, 200),
                statsY + 40,
                Phaser.Math.Between(2, 5),
                0xFFD700,
                0.8
            );

            this.tweens.add({
                targets: particle,
                y: particle.y - Phaser.Math.Between(50, 150),
                alpha: 0,
                duration: 2000,
                delay: i * 50,
                onComplete: () => particle.destroy()
            });
        }
    }

    createButtons() {
        const centerX = this.scale.width / 2;
        const buttonY = this.scale.height - 150;

        // Retry button - go directly to gameplay
        this.createButton(centerX - 100, buttonY, 'RETRY', () => {
            audioEngine.playClick();
            this.cameras.main.fadeOut(300);
            this.time.delayedCall(300, () => {
                this.scene.start('GameplayScene', { distance: 0, correctAnswers: 0 });
            });
        });

        // Main menu button - also go to gameplay for now (testing)
        this.createButton(centerX + 100, buttonY, 'MENU', () => {
            audioEngine.playClick();
            this.cameras.main.fadeOut(300);
            this.time.delayedCall(300, () => {
                this.scene.start('GameplayScene', { distance: 0, correctAnswers: 0 });
            });
        });
    }

    createButton(x, y, text, callback) {
        // Button background
        const button = this.add.graphics();
        button.fillStyle(0x333333, 0.9);
        button.fillRoundedRect(x - 80, y - 25, 160, 50, 10);

        // Hit area
        const hitArea = this.add.rectangle(x, y, 160, 50, 0x000000, 0)
            .setInteractive({ useHandCursor: true });

        // Button text
        const buttonText = this.add.text(x, y, text, {
            fontSize: '20px',
            fontFamily: 'Courier New, monospace',
            color: '#FFFFFF',
            fontStyle: 'bold'
        }).setOrigin(0.5);

        // Hover effects
        hitArea.on('pointerover', () => {
            button.clear();
            button.fillStyle(0x555555, 0.9);
            button.fillRoundedRect(x - 80, y - 25, 160, 50, 10);
            buttonText.setColor('#FF6666');
        });

        hitArea.on('pointerout', () => {
            button.clear();
            button.fillStyle(0x333333, 0.9);
            button.fillRoundedRect(x - 80, y - 25, 160, 50, 10);
            buttonText.setColor('#FFFFFF');
        });

        hitArea.on('pointerdown', callback);
    }

    createAmbience() {
        // Low eerie drone
        if (audioEngine.initialized) {
            // Very quiet ambient
        }
    }
}
